import React, { useState, useMemo } from "react";
import { Check, ChevronsUpDown, ChevronDown, X, Search, XCircle } from "lucide-react";
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Input,
  ScrollArea,
  Badge,
} from "@musa.tech/design-system";
import { cn } from "@/lib/utils";

interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectProps {
  options: MultiSelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  className?: string;
  "aria-label"?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  inline?: boolean;
  hideBadges?: boolean;
}

export const MultiSelect = ({
  options,
  value,
  onChange,
  placeholder = "Selecionar opções",
  className,
  "aria-label": ariaLabel,
  searchPlaceholder = "Buscar...",
  emptyMessage = "Nenhuma opção encontrada.",
  inline = false,
  hideBadges = false,
}: MultiSelectProps) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const inputRef = React.useRef<HTMLInputElement>(null);

  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return options;
    const searchLower = searchQuery.trim().toLowerCase();
    return options.filter((option) =>
      option.label.toLowerCase().includes(searchLower),
    );
  }, [options, searchQuery]);

  const selectedOptions = useMemo(() => {
    return options.filter((option) => value.includes(option.value));
  }, [options, value]);

  const handleSelect = (optionValue: string) => {
    onChange(
      value.includes(optionValue)
        ? value.filter((v) => v !== optionValue)
        : [...value, optionValue],
    );
  };

  const handleRemove = (optionValue: string) => {
    onChange(value.filter((v) => v !== optionValue));
  };

  const handleClearAll = () => onChange([]);

  const getDisplayText = () => {
    if (value.length === 0) return placeholder;
    if (value.length === 1) return selectedOptions[0]?.label || placeholder;
    return `${value.length} opções selecionadas`;
  };

  React.useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  React.useEffect(() => {
    if (!open) setSearchQuery("");
  }, [open]);

  const searchBar = (
    <div className="flex items-center border-b px-3 py-2">
      <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
      <Input
        ref={inputRef}
        placeholder={searchPlaceholder}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        className="h-8 border-0 bg-transparent p-0 text-sm placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:ring-offset-0"
      />
      {searchQuery && (
        <X
          className="h-4 w-4 shrink-0 opacity-50 hover:opacity-100 cursor-pointer"
          onClick={() => setSearchQuery("")}
        />
      )}
    </div>
  );

  const itemList = filteredOptions.map((option) => (
    <div
      key={option.value}
      onClick={() => handleSelect(option.value)}
      className={cn(
        "relative flex min-w-0 cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors",
        "hover:bg-accent hover:text-accent-foreground",
        value.includes(option.value) && "bg-accent",
      )}
    >
      <Check
        className={cn(
          "mr-2 h-4 w-4 shrink-0",
          value.includes(option.value) ? "opacity-100" : "opacity-0",
        )}
      />
      <span className="flex-1 min-w-0 truncate">{option.label}</span>
    </div>
  ));

  const emptyState = (
    <div className="py-6 text-center text-sm text-muted-foreground">
      {searchQuery.trim()
        ? `Nenhum resultado para "${searchQuery}"`
        : emptyMessage}
    </div>
  );

  const clearButton = value.length > 0 && !searchQuery.trim() && (
    <div
      onClick={handleClearAll}
      className="relative flex cursor-pointer select-none items-center justify-center gap-2 rounded-sm px-2 py-1.5 text-sm text-destructive outline-none transition-colors hover:bg-destructive/10"
    >
      <X className="h-4 w-4" />
      Limpar seleção
    </div>
  );

  const footer = filteredOptions.length > 0 && (
    <div className="border-t px-3 py-2 text-xs text-muted-foreground">
      {searchQuery.trim()
        ? `${filteredOptions.length} resultado${filteredOptions.length !== 1 ? "s" : ""}`
        : `${options.length} opç${options.length !== 1 ? "ões" : "ão"}`}
      {value.length > 0 &&
        ` • ${value.length} selecionado${value.length !== 1 ? "s" : ""}`}
    </div>
  );

  if (inline) {
    return (
      <div className={cn("w-full space-y-1", className)}>
        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          aria-label={ariaLabel}
          className={cn(
            "flex w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm",
            "hover:bg-accent hover:text-accent-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
            value.length === 0 && "text-muted-foreground",
          )}
        >
          <span className="flex-1 min-w-0 truncate text-left">{getDisplayText()}</span>
          <ChevronDown
            className={cn("ml-2 h-4 w-4 shrink-0 transition-transform", open && "rotate-180")}
          />
        </button>

        {open && (
          <div className="w-full overflow-x-hidden rounded-md border bg-popover shadow-sm">
            {searchBar}
            <div className="overflow-y-auto max-h-[300px]">
              {filteredOptions.length === 0 ? (
                emptyState
              ) : (
                <div className="p-1">
                  {clearButton}
                  {itemList}
                </div>
              )}
            </div>
            {footer}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={cn("relative", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-label={ariaLabel}
            className="w-full justify-between h-10 border border-input rounded-lg px-3 text-sm focus:ring-2 focus:ring-offset-1 focus:ring-ring bg-background"
          >
            <span className="truncate">{getDisplayText()}</span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          side="bottom"
          align="start"
          className="p-0 bg-popover border shadow-md z-50 w-auto min-w-[var(--radix-popover-trigger-width)] max-w-[min(28rem,90vw)]"
        >
          {searchBar}
          <ScrollArea className="max-h-[300px]">
            {filteredOptions.length === 0 ? (
              emptyState
            ) : (
              <div className="p-1">
                {clearButton}
                {itemList}
              </div>
            )}
          </ScrollArea>
          {footer}
        </PopoverContent>
      </Popover>

      {!hideBadges && selectedOptions.length > 0 && selectedOptions.length <= 3 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {selectedOptions.map((option) => (
            <Badge
              key={option.value}
              size="small"
              variant="primary"
              className="max-w-[200px] overflow-hidden [&>p]:min-w-0 [&>p]:truncate"
              title={option.label}
              iconRight={
                <XCircle
                  size={14}
                  className="shrink-0 cursor-pointer text-primary-600 hover:text-primary-900 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove(option.value);
                  }}
                />
              }
            >
              {option.label}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
};
